import * as signalR from "@microsoft/signalr";
import { trackEvent } from "./ga-events";

var connection = null;
var connected = false;
var processingCount = 0;
var bannerState = 'processing';
var feedRefreshTimer = null;
var feedChromeRefreshTimer = null;
var liveShellRefreshTimer = null;
var statusRefreshTimer = null;
var delayTimer = null;
var FEED_REFRESH_DEBOUNCE_MS = 1500;
var STATUS_REFRESH_DEBOUNCE_MS = 1000;
var DELAY_THRESHOLD_MS = 90000;
var recentEvents = new Map();
var DEDUP_TTL_MS = 15000;
var INITIAL_RETRY_DELAYS = [0, 2000, 5000, 10000];

function getToast() {
    return window.Stackora && window.Stackora.toast;
}

function isDuplicate(itemId, eventType) {
    var key = String(itemId || '') + ':' + eventType;
    var now = Date.now();

    // Prune expired entries on every call
    recentEvents.forEach(function (timestamp, entryKey) {
        if (now - timestamp > DEDUP_TTL_MS) {
            recentEvents.delete(entryKey);
        }
    });

    if (recentEvents.has(key)) {
        return true;
    }

    recentEvents.set(key, now);
    return false;
}

function scheduleFeedRefresh() {
    if (feedRefreshTimer) {
        clearTimeout(feedRefreshTimer);
    }

    feedRefreshTimer = setTimeout(function () {
        feedRefreshTimer = null;
        if (window.Stackora && typeof window.Stackora.refreshDashboardFeed === 'function') {
            window.Stackora.refreshDashboardFeed();
        }
    }, FEED_REFRESH_DEBOUNCE_MS);
}

function scheduleFeedChromeRefresh() {
    if (feedChromeRefreshTimer) {
        clearTimeout(feedChromeRefreshTimer);
    }

    feedChromeRefreshTimer = setTimeout(function () {
        feedChromeRefreshTimer = null;
        if (window.Stackora && typeof window.Stackora.refreshDashboardFeedChrome === 'function') {
            window.Stackora.refreshDashboardFeedChrome();
        }
    }, FEED_REFRESH_DEBOUNCE_MS);
}

function scheduleStatusRegionRefresh() {
    if (statusRefreshTimer) {
        clearTimeout(statusRefreshTimer);
    }

    statusRefreshTimer = setTimeout(function () {
        statusRefreshTimer = null;
        if (window.Stackora && typeof window.Stackora.refreshDashboardStatusRegion === 'function') {
            window.Stackora.refreshDashboardStatusRegion();
        }
    }, STATUS_REFRESH_DEBOUNCE_MS);
}

function scheduleLiveShellRefresh() {
    if (liveShellRefreshTimer) {
        clearTimeout(liveShellRefreshTimer);
    }

    liveShellRefreshTimer = setTimeout(function () {
        liveShellRefreshTimer = null;
        if (window.Stackora && typeof window.Stackora.refreshDashboardLiveShell === 'function') {
            window.Stackora.refreshDashboardLiveShell();
        }
    }, FEED_REFRESH_DEBOUNCE_MS);
}

function clearDelayTimer() {
    if (delayTimer) {
        clearTimeout(delayTimer);
        delayTimer = null;
    }
}

function armDelayTimer() {
    clearDelayTimer();
    if (processingCount <= 0) {
        return;
    }

    delayTimer = setTimeout(function () {
        delayTimer = null;
        if (processingCount > 0) {
            bannerState = 'delayed';
            syncProcessingBanner();
        }
    }, DELAY_THRESHOLD_MS);
}

function getProcessingCopy() {
    if (processingCount <= 0) {
        return null;
    }

    var noun = processingCount === 1 ? 'email' : 'emails';
    if (bannerState === 'delayed') {
        return {
            title: 'Still working on ' + processingCount + ' ' + noun + '.',
            meta: 'Attachments and long threads can take a few minutes. This page updates automatically.'
        };
    }

    return {
        title: 'We\'ve received ' + processingCount + ' ' + noun + '.',
        meta: 'Most summaries are ready in under a minute. This page updates automatically.'
    };
}

function syncProcessingBanner() {
    var banner = document.getElementById('processing-banner');
    if (!banner) {
        return;
    }

    var title = document.getElementById('processing-banner-text');
    var meta = document.getElementById('processing-banner-meta');
    var copy = getProcessingCopy();

    if (!copy) {
        banner.hidden = true;
        return;
    }

    if (title) {
        title.textContent = copy.title;
    }

    if (meta) {
        meta.textContent = copy.meta;
    }

    banner.hidden = false;
}

function incrementProcessingCount() {
    processingCount += 1;
    bannerState = 'processing';
    armDelayTimer();
    syncProcessingBanner();
}

function ensureProcessingVisible() {
    if (processingCount <= 0) {
        processingCount = 1;
    }

    if (bannerState !== 'delayed') {
        bannerState = 'processing';
    }

    armDelayTimer();
    syncProcessingBanner();
}

function completeProcessingCount() {
    processingCount = Math.max(0, processingCount - 1);
    if (processingCount <= 0) {
        clearDelayTimer();
        bannerState = 'processing';
    } else if (bannerState !== 'delayed') {
        armDelayTimer();
    }

    syncProcessingBanner();
}

function onEmailReceived(event) {
    if (isDuplicate(event.itemId, 'received')) {
        return;
    }

    incrementProcessingCount();
    scheduleStatusRegionRefresh();
    scheduleFeedChromeRefresh();
    scheduleLiveShellRefresh();

    var toast = getToast();
    if (toast) {
        toast.success('Email received. We\'re processing it now.');
    }
}

function onEmailStatusChanged(event) {
    if (isDuplicate(event.itemId, event.status)) {
        return;
    }

    scheduleLiveShellRefresh();

    var toast = getToast();
    switch (event.status) {
        case 'Processing':
            ensureProcessingVisible();
            scheduleFeedChromeRefresh();
            break;
        case 'ForwardingVerification':
            completeProcessingCount();
            scheduleFeedRefresh();
            scheduleStatusRegionRefresh();
            if (toast) {
                toast.success('Forwarding confirmation ready.');
            }
            break;
        case 'OnboardingPipelineTest':
            completeProcessingCount();
            scheduleStatusRegionRefresh();
            scheduleFeedChromeRefresh();
            if (toast) {
                toast.success('Verification message received. Forwarding is confirmed.');
            }
            trackEvent('dashboard_pipeline_test_received', { confirmation_surface: 'status_region' });
            break;
        case 'FailedRetryable':
            ensureProcessingVisible();
            bannerState = 'delayed';
            syncProcessingBanner();
            scheduleFeedChromeRefresh();
            if (toast) {
                toast.info('Still working on this email. We\'ll keep trying.');
            }
            break;
        case 'FailedPermanent':
            completeProcessingCount();
            scheduleFeedRefresh();
            if (toast) {
                toast.error('We couldn\'t finish that email. Try forwarding it again.');
            }
            break;
        case 'Quarantined':
            completeProcessingCount();
            scheduleFeedRefresh();
            if (toast) {
                toast.info('An email was quarantined as possible spam.');
            }
            break;
        case 'DroppedSpam':
            completeProcessingCount();
            scheduleFeedRefresh();
            if (toast) {
                toast.info('A suspected spam email was dropped.');
            }
            break;
        case 'DroppedLimitExceeded':
            completeProcessingCount();
            scheduleStatusRegionRefresh();
            scheduleFeedRefresh();
            if (toast) {
                toast.info('Your email limit has been reached. Upgrade your plan to keep receiving emails.', { duration: 8000 });
            }
            trackEvent('email_limit_reached', {});
            break;
        case 'Summarized':
        case 'Processed':
            completeProcessingCount();
            scheduleFeedRefresh();
            if (toast) {
                toast.success('Your summary is ready.');
            }
            break;
        default:
            // Unknown terminal states should still clear processing
            completeProcessingCount();
            scheduleFeedRefresh();
            break;
    }
}

function startConnection(attempt) {
    connection.start()
        .then(function () {
            connected = true;
            syncProcessingBanner();
        })
        .catch(function (error) {
            console.warn('SignalR connection failed (attempt ' + (attempt + 1) + '):', error);
            if (attempt < INITIAL_RETRY_DELAYS.length - 1) {
                var delay = INITIAL_RETRY_DELAYS[attempt + 1];
                setTimeout(function () { startConnection(attempt + 1); }, delay);
            } else {
                var toast = getToast();
                if (toast) {
                    toast.info('Live updates unavailable. Refresh the page to try again.', { duration: 5000 });
                }
            }
        });
}

function seedProcessingCountFromServer() {
    var banner = document.getElementById('processing-banner');
    if (!banner) return;
    var serverCount = parseInt(banner.getAttribute('data-processing-count'), 10) || 0;
    if (serverCount > 0) {
        processingCount = serverCount;
        bannerState = 'processing';
        armDelayTimer();
        syncProcessingBanner();
    }
}

function start() {
    // Only open a SignalR connection on the dashboard page
    if (!document.getElementById('dash-status-region')) {
        return;
    }

    seedProcessingCountFromServer();

    connection = new signalR.HubConnectionBuilder()
        .withUrl('/hubs/email-processing')
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

    connection.on('emailReceived', onEmailReceived);
    connection.on('emailStatusChanged', onEmailStatusChanged);

    connection.onreconnecting(function () {
        connected = false;
    });

    connection.onreconnected(function () {
        connected = true;
        seedProcessingCountFromServer();
        scheduleFeedRefresh();
        scheduleLiveShellRefresh();
        scheduleStatusRegionRefresh();
    });

    connection.onclose(function () {
        connected = false;
        var toast = getToast();
        if (toast) {
            toast.info('Live updates are temporarily unavailable.', { duration: 4000 });
        }
    });

    startConnection(0);
}

window.Stackora = window.Stackora || {};
window.Stackora.signalr = {
    isConnected: function () { return connected; },
    getProcessingCount: function () { return processingCount; },
    syncProcessingBanner: syncProcessingBanner
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
} else {
    start();
}
